import * as React from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Switch from '@mui/material/Switch';
import Collapse from '@mui/material/Collapse';
import ButtonBase from '@mui/material/ButtonBase';
import { alpha } from '@mui/material/styles';

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';

import { useApp } from '../../../context';

/**
 * The navigation drawer.
 *
 * Rebuilt from a plain MUI `List` into the tiled layout: a dark-mode row, a
 * grid of destinations, then the platform taxonomy as accordions.
 *
 * THE TILES ARE THIS APP'S, NOT THE REFERENCE DESIGN'S. The reference showed
 * Map / Book Stay / Rent Car / Book Flight — none of which exist here, and a
 * tile that goes nowhere is worse than one fewer tile. What replaced them is
 * what a signed-in traveller actually has: their itinerary, their bookings,
 * who they follow, and a way out.
 *
 * CATEGORIES COME FROM THE PLATFORM TAXONOMY, live from
 * `service_provider_filters/categories` via the app context — the same
 * document the admin console edits and the home feed groups by. Hardcoding the
 * reference design's seven groups would have invented a second taxonomy that
 * matches no listing.
 */

const TILE_SIZE = 92;

function Tile({ icon, label, to, onClick, onNavigate }) {
  const content = (
    <>
      <Box sx={{ color: 'text.secondary', display: 'flex', mb: 1 }}>{icon}</Box>
      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'center' }}>
        {label}
      </Typography>
    </>
  );

  const sx = {
    flexDirection: 'column',
    height: TILE_SIZE,
    borderRadius: 2.5,
    border: 1,
    borderColor: 'divider',
    backgroundColor: (theme) =>
      theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, 0.04)
        : alpha(theme.palette.common.black, 0.03),
    transition: (theme) => theme.transitions.create(['border-color', 'background-color']),
    '&:hover': { borderColor: 'primary.main' },
  };

  // A link when it navigates, a button when it acts. Logout is not a place.
  return to ? (
    <ButtonBase component={Link} to={to} onClick={onNavigate} sx={sx}>
      {content}
    </ButtonBase>
  ) : (
    <ButtonBase onClick={onClick} sx={sx}>
      {content}
    </ButtonBase>
  );
}

function CategoryRow({ category, filters, onNavigate }) {
  const [open, setOpen] = React.useState(false);

  return (
    <Box sx={{ mb: 1 }}>
      <ButtonBase
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        sx={{
          width: '100%',
          justifyContent: 'space-between',
          px: 1.75,
          py: 1.25,
          borderRadius: 2,
          border: 1,
          borderColor: open ? 'primary.main' : 'divider',
          backgroundColor: (theme) =>
            open ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {category}
        </Typography>
        <ExpandMoreRoundedIcon
          sx={{
            fontSize: '1.2rem',
            color: 'text.secondary',
            transform: open ? 'rotate(180deg)' : 'none',
            transition: (theme) => theme.transitions.create('transform'),
          }}
        />
      </ButtonBase>

      <Collapse in={open} unmountOnExit>
        {/* The rail is the design's, and it earns its place: it ties the
            sub-filters to the category they belong to once several are open. */}
        <Box sx={{ ml: 2, pl: 2, borderLeft: 2, borderColor: 'primary.main', mt: 1 }}>
          <Box
            component={Link}
            to={`/search?type=${encodeURIComponent(category)}`}
            onClick={onNavigate}
            sx={{
              display: 'block',
              py: 0.75,
              color: 'text.primary',
              textDecoration: 'none',
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            All {category}
          </Box>
          {filters.map((filter) => (
            <Box
              key={filter}
              component={Link}
              to={`/search?type=${encodeURIComponent(category)}&filter=${encodeURIComponent(filter)}`}
              onClick={onNavigate}
              sx={{
                display: 'block',
                py: 0.75,
                color: 'text.secondary',
                textDecoration: 'none',
                fontSize: 14,
                '&:hover': { color: 'primary.main' },
              }}
            >
              {filter}
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

export default function DrawerMenu({ logo, mode, onToggleMode, onClose, user, onLogout, onSignIn, onRegister }) {
  const { serviceFilters } = useApp();

  const categories = Array.isArray(serviceFilters?.categories) ? serviceFilters.categories : [];
  const subFilters = serviceFilters?.subFilters ?? {};

  const tiles = user
    ? [
        { key: 'home', label: 'Home', icon: <HomeOutlinedIcon />, to: '/' },
        { key: 'itinerary', label: 'Itinerary', icon: <MapOutlinedIcon />, to: '/itinerary-list' },
        { key: 'bookings', label: 'Bookings', icon: <EventNoteOutlinedIcon />, to: '/booking-list' },
        { key: 'following', label: 'Following', icon: <FavoriteBorderRoundedIcon />, to: '/following' },
        { key: 'logout', label: 'Logout', icon: <LogoutRoundedIcon />, onClick: onLogout },
        { key: 'info', label: 'Useful Info', icon: <InfoOutlinedIcon />, to: '/useful-info' },
      ]
    : [
        // Signed out, the four account tiles have nothing behind them. Offering
        // the way in is more use than showing four tiles that bounce.
        { key: 'home', label: 'Home', icon: <HomeOutlinedIcon />, to: '/' },
        { key: 'signin', label: 'Sign in', icon: <LoginRoundedIcon />, onClick: onSignIn },
        { key: 'register', label: 'Register', icon: <PersonAddAltRoundedIcon />, onClick: onRegister },
        { key: 'info', label: 'Useful Info', icon: <InfoOutlinedIcon />, to: '/useful-info' },
      ];

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* ── Brand and close ─────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 2 }}>
        <Box component="img" src={logo} alt="Vhakacha" sx={{ height: 30, width: 'auto' }} />
        <IconButton onClick={onClose} aria-label="Close menu" size="small">
          <CloseRoundedIcon />
        </IconButton>
      </Box>

      {/* ── Dark mode ───────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2.5,
          py: 1.5,
          borderTop: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <DarkModeOutlinedIcon sx={{ fontSize: '1.2rem', color: 'primary.main' }} />
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            Dark Mode
          </Typography>
        </Box>
        {/* `slotProps.input`, not `inputProps` — MUI v9 moved it, and the old
            prop is dropped silently, leaving the switch with no accessible
            name at all. */}
        <Switch
          checked={mode === 'dark'}
          onChange={onToggleMode}
          slotProps={{ input: { 'aria-label': 'Dark mode' } }}
        />
      </Box>

      {/* ── Tiles ───────────────────────────────────────── */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 1.25,
          px: 2.5,
          py: 2.5,
        }}
      >
        {tiles.map((tile) => (
          <Tile
            key={tile.key}
            icon={tile.icon}
            label={tile.label}
            to={tile.to}
            onClick={tile.onClick}
            onNavigate={onClose}
          />
        ))}
      </Box>

      {/* ── Categories ──────────────────────────────────── */}
      {categories.length > 0 && (
        <Box sx={{ px: 2.5, pb: 2, flex: 1, overflowY: 'auto' }}>
          <Typography
            variant="overline"
            sx={{ color: 'text.secondary', fontWeight: 800, letterSpacing: '0.14em' }}
          >
            Categories
          </Typography>
          <Box sx={{ mt: 1 }}>
            {categories.map((category) => (
              <CategoryRow
                key={category}
                category={category}
                filters={Array.isArray(subFilters[category]) ? subFilters[category] : []}
                onNavigate={onClose}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* ── Company ─────────────────────────────────────── */}
      <Box sx={{ mt: 'auto', px: 2.5, py: 2, borderTop: 1, borderColor: 'divider' }}>
        <Typography
          variant="overline"
          sx={{ color: 'text.secondary', fontWeight: 800, letterSpacing: '0.14em' }}
        >
          Company
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1 }}>
          <LocationOnRoundedIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />
          <Typography variant="body2" color="text.secondary">
            Zimbabwe, Africa
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          © {new Date().getFullYear()} Vhakacha
        </Typography>
        {user && (
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            title={user.email || user.displayName}
            sx={{ display: 'block', mt: 0.5 }}
          >
            Signed in as {user.email || user.displayName}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
